import React, { useCallback, useEffect, useMemo, useRef } from 'react'
import { TextField, CircularProgress } from '@material-ui/core'
import Autocomplete from '@material-ui/lab/Autocomplete'
import { makeStyles } from '@material-ui/core/styles'
import useAthenaLookup from '../hooks/useAthenaLookup'

const useStyles = makeStyles(theme => ({
  root: {
    width: '100%'
  },
  listbox: {
    maxHeight: 250,
    overflow: 'auto'
  },
  noOptions: {
    padding: theme.spacing(2),
    color: theme.palette.text.secondary
  },
  loading: {
    padding: theme.spacing(2),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  input: {
    padding: theme.spacing(1)
  },
  inputRoot: {
    padding: theme.spacing(1)
  }
}))

function AthenaLookupDropdown ({
  id,
  lookup,
  label = 'Search',
  placeholder = 'Start typing to search...',
  required = false,
  disabled = false,
  width = '100%',
  variant = 'standard',
  debounceMs,
  accessToken,
  credentials,
  displayField = 'DISPLAY',
  valueField = 'VALUE',
  filters = {},
  onChange,
  customError = null,
  clearTrigger = false,
  initialValue = null
}) {
  const classes = useStyles()
  const prevClearTriggerRef = useRef(clearTrigger)
  const inputRef = useRef(null)

  const {
    loading,
    options,
    inputValue,
    selectedValue,
    touched,
    isIndexNotFound,
    handleInputChange,
    handleChange,
    handleOpen,
    handleBlur,
    resetTouched,
    setValue,
    setInputValueDirect,
    searchItems
  } = useAthenaLookup({
    lookup,
    accessToken,
    credentials,
    debounceMs,
    filters
  })

  const sortedOptions = useMemo(() => {
    if (inputValue == null || inputValue.trim() === '') {
      return [...options].sort((a, b) => {
        const aValue = a?.[displayField] || ''
        const bValue = b?.[displayField] || ''
        return aValue.localeCompare(bValue, undefined, { numeric: true, sensitivity: 'base' })
      })
    }
    return options
  }, [displayField, inputValue, options])

  const getError = useCallback(() => {
    if (!touched) return null
    if (customError) return customError
    if (required && !selectedValue) return `${label} must be selected`
    return null
  }, [customError, label, required, selectedValue, touched])

  useEffect(() => {
    if (clearTrigger !== prevClearTriggerRef.current) {
      setValue(null)
      if (onChange) {
        onChange(null)
      }
      handleInputChange('')
      resetTouched()
      searchItems('')
    }
    prevClearTriggerRef.current = clearTrigger
  }, [clearTrigger, handleInputChange, onChange, resetTouched, searchItems, setValue])

  const initialValueProcessed = useRef(false)
  useEffect(() => {
    if (initialValue && !initialValueProcessed.current) {
      initialValueProcessed.current = true
      setValue(initialValue)
      setInputValueDirect(initialValue[displayField] || '')
    }
  }, [displayField, initialValue, setInputValueDirect, setValue])

  useEffect(() => {
    const inputElement = inputRef.current
    if (!inputElement) return undefined

    const form = inputElement.closest('form')
    if (!form) return undefined

    const handleFormSubmit = () => {
      if (required && !selectedValue) {
        handleBlur()
      }
    }

    form.addEventListener('submit', handleFormSubmit)
    return () => {
      form.removeEventListener('submit', handleFormSubmit)
    }
  }, [handleBlur, required, selectedValue])

  const handleAutocompleteChange = useCallback((event, newValue) => {
    handleChange(newValue, onChange)
  }, [handleChange, onChange])

  const handleAutocompleteInputChange = useCallback((event, newInputValue) => {
    handleInputChange(newInputValue)
  }, [handleInputChange])

  const getOptionLabel = useCallback((option) => {
    return option?.[displayField] || ''
  }, [displayField])

  const getOptionSelected = useCallback((option, value) => {
    return option?.[valueField] === value?.[valueField]
  }, [valueField])

  const renderOption = useCallback((option) => {
    return (
      <div>
        {option[displayField]}
      </div>
    )
  }, [displayField])

  return (
    <Autocomplete
      id={id}
      className={classes.root}
      style={{ width }}
      value={selectedValue}
      onChange={handleAutocompleteChange}
      inputValue={inputValue}
      onInputChange={handleAutocompleteInputChange}
      options={sortedOptions}
      getOptionLabel={getOptionLabel}
      getOptionSelected={getOptionSelected}
      loading={loading}
      disabled={disabled}
      onOpen={handleOpen}
      filterOptions={(x) => x}
      noOptionsText={isIndexNotFound ? 'No Data' : 'No options'}
      classes={{
        listbox: classes.listbox,
        noOptions: classes.noOptions,
        loading: classes.loading,
        ...(variant !== 'filled' && {
          input: classes.input,
          inputRoot: classes.inputRoot
        })
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          inputRef={(ref) => {
            inputRef.current = ref
            if (params.inputRef) {
              params.inputRef(ref)
            }
          }}
          label={label}
          placeholder={placeholder}
          required={required}
          error={!!getError()}
          helperText={getError()}
          variant={variant}
          onBlur={handleBlur}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? (
                  <CircularProgress color='inherit' size={20} />
                ) : null}
                {params.InputProps.endAdornment}
              </>
            )
          }}
        />
      )}
      renderOption={renderOption}
    />
  )
}

export default AthenaLookupDropdown
